import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { httpService } from "../../../../../base/services/httpService.service";
import { PlanDetail } from "../planSection/PlanDetail";
import {
   ANNUAL_BILLING_NOTE,
   formatPrice,
   formatTrialDays,
   getPlanPrice,
   getSharedTrialDays
} from "../../planPricing";
import {
   AccountLevel,
   filterPlansByAccountLevel,
   getAccountLevelCopy,
   getOrganizationLevel,
   getPlanAccountLevel
} from "../../accountLevel";

// The package step, laid out as the two tracks side by side: the retail plan on
// the left in its own warm colour, the organization plans on the right in the
// blue-purple the rest of the flow is themed in. Every plan is in view at once,
// so nothing has to be chosen before the profile is filled in — the plan that
// is picked is what says which level the account registers under, which is why
// there is no separate account type step (see accountLevel.js).
//
// An organization that is already registered (it passes its `organizationLevel`)
// is offered every plan of either kind in a single row instead: the level
// follows the plan it picks, so it can move across.
//
// Each packet's trial is printed only while `offersTrial` holds: an
// organization gets one free trial, ever, and one that has had it is sold the
// same plans without it (components/Billing/billingActions.js, offersTrial).
//
// `enterpriseOption` is Enterprise, picked like a plan under the priced ones
// (components/Billing/EnterpriseOption.jsx): it is never in getPackets, and
// what it is offered as depends on the caller — asked for at sign up, its terms
// on the billing page.

/** A limit of 0 is the API's way of saying the plan doesn't cap it. */
const asLimit = (limit) => (limit ? limit.toLocaleString() : "Unlimited");

const pluralize = (limit, word) => (limit === 1 ? word : `${word}s`);

// What a plan card lists, in the order the lines read best. A plan describes
// itself at greater length in its details modal, which is where the rest of it
// belongs — a card that prints every line of it is a card nobody reads.
const getPlanLimits = (plan, accountLevel) => {
   const workspaces = `${asLimit(plan.workspaceLimit)} ${pluralize(
      plan.workspaceLimit,
      "workspace"
   )}`;
   const dashboards = `${asLimit(plan.projectDashboardLimit)} project ${pluralize(
      plan.projectDashboardLimit,
      "dashboard"
   )}`;

   // A business account is told what its plan lets it do. The member and
   // storage limits an organization's plan is sold on would be claims it
   // cannot act on — see the level's planFeatures in accountLevel.js.
   if (accountLevel === AccountLevel.Business) {
      return [workspaces, dashboards, ...getAccountLevelCopy(accountLevel).planFeatures];
   }

   return [
      workspaces,
      `${asLimit(plan.membersLimit)} ${pluralize(plan.membersLimit, "user")} per workspace`,
      dashboards,
      plan.databaseLimit ? `${plan.databaseLimit} GB data storage` : "Unlimited data storage"
   ];
};

// The packets are asked for under the level that subscribes to them — 0 for an
// organization, 1 for a business — because the endpoint only returns the
// customer feedback plan for level 1 and only the general ones for level 0.
// Filtered again in case a level comes back unfiltered, and emptied on failure
// so a packet the API can't serve never empties the other column with it.
const fetchPlans = (accountLevel) =>
   httpService
      .get(`/Account/getPackets?organizationLevel=${getOrganizationLevel(accountLevel)}`)
      .then((res) => filterPlansByAccountLevel(res.data, accountLevel))
      .catch(() => []);

/** Cheapest first, so a plan lineup reads as steps up. */
const byMonthlyPrice = (a, b) => (a.monthlyPrice ?? 0) - (b.monthlyPrice ?? 0);

/** The tracks this step offers: both, to a sign up and to a registered organization alike. */
const OFFERED_LEVELS = [AccountLevel.Business, AccountLevel.Organization];

// A card holds a list and the details link, so it is a radio rather than a
// button — a button may not contain either.
function PlanCard({ plan, isYearly, isSelected, onSelect, accountLevel, trialLabel }) {
   const select = () => onSelect(plan);

   const handleKeyDown = (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      select();
   };

   return (
      <div
         role="radio"
         tabIndex={0}
         aria-checked={isSelected}
         aria-label={plan.name}
         onClick={select}
         onKeyDown={handleKeyDown}
         className={`package-choice__plan package-choice__plan--${accountLevel}${
            isSelected ? " package-choice__plan--selected" : ""
         }`}
      >
         <div className="package-choice__plan-head">
            <span className="package-choice__plan-radio" aria-hidden="true" />
            <span className="package-choice__plan-name">{plan.name}</span>
         </div>
         {trialLabel && <span className="package-choice__plan-trial">{trialLabel}</span>}
         <div className="package-choice__plan-price">
            <b>{formatPrice(getPlanPrice(plan, isYearly))}</b>
            <small>/month</small>
         </div>
         {isYearly && <span className="package-choice__plan-terms">{ANNUAL_BILLING_NOTE}</span>}
         <ul className="package-choice__plan-limits">
            {getPlanLimits(plan, accountLevel).map((limit) => (
               <li key={limit}>
                  <AiOutlineCheckCircle />
                  <span>{limit}</span>
               </li>
            ))}
         </ul>
         {/* The details modal is a React child of this card, so a click inside
             it bubbles up and would pick the plan behind. Kept to itself. */}
         <div className="package-choice__plan-details" onClick={(event) => event.stopPropagation()}>
            <PlanDetail planData={plan} accountLevel={accountLevel} isYearly={isYearly} />
         </div>
      </div>
   );
}

// One side of the split: who the track is for and the plans it sells. The panel
// lights up when the plan that is picked is one of its own.
function PackagePanel({
   level,
   plans,
   isYearly,
   isLoading,
   selectedPlanId,
   onSelect,
   offersTrial,
   children
}) {
   const copy = getAccountLevelCopy(level);
   const holdsSelection = plans.some((plan) => plan.id === selectedPlanId);
   // The trial is each packet's own. A length the whole track shares is said
   // once, over the plans; lengths that differ are said on each card instead.
   const sharedTrialLabel = offersTrial ? formatTrialDays(getSharedTrialDays(plans)) : null;

   return (
      <div
         className={`package-choice__panel package-choice__panel--${level}${
            holdsSelection ? " package-choice__panel--selected" : ""
         }`}
      >
         <div className="package-choice__panel-head">
            <h2 className="package-choice__panel-name">{copy.name}</h2>
            <p className="package-choice__panel-tagline">{copy.tagline}</p>
         </div>

         <div className="package-choice__panel-body">
            <div className="package-choice__plans-header">
               {copy.plansLabel && <span>{copy.plansLabel}</span>}
               {sharedTrialLabel && (
                  <span className="package-choice__trial">{sharedTrialLabel}</span>
               )}
            </div>

            {isLoading ? (
               <div className="package-choice__plans-loading">
                  <Spin />
               </div>
            ) : (
               <div className="package-choice__plans" role="radiogroup" aria-label={copy.name}>
                  {plans.map((plan) => (
                     <PlanCard
                        key={plan.id}
                        plan={plan}
                        isYearly={isYearly}
                        isSelected={plan.id === selectedPlanId}
                        onSelect={onSelect}
                        accountLevel={level}
                        trialLabel={
                           !offersTrial || sharedTrialLabel ? null : formatTrialDays(plan.trialDays)
                        }
                     />
                  ))}
               </div>
            )}

            {children}
         </div>
      </div>
   );
}

// Every plan in one row, cheapest first, for an organization that is already
// registered. It picked a kind of account when it signed up, but the level
// follows the plan, so it is offered both kinds' plans without the panels that
// introduce them: each card says what its plan lets the account do, in its own
// kind's colours. A trial only while the organization still has its one.
function SingleRowPlans({
   plans,
   isYearly,
   isLoading,
   selectedPlanId,
   onSelect,
   offersTrial,
   enterpriseOption
}) {
   return (
      <div className="package-choice__single">
         {isLoading ? (
            <div className="package-choice__plans-loading">
               <Spin />
            </div>
         ) : (
            <div className="package-choice__single-plans" role="radiogroup" aria-label="Plans">
               {plans.map((plan) => (
                  <PlanCard
                     key={plan.id}
                     plan={plan}
                     isYearly={isYearly}
                     isSelected={plan.id === selectedPlanId}
                     onSelect={onSelect}
                     accountLevel={getPlanAccountLevel(plan)}
                     trialLabel={offersTrial ? formatTrialDays(plan.trialDays) : null}
                  />
               ))}
            </div>
         )}

         {enterpriseOption}
      </div>
   );
}

function PackageChoice({
   isYearly,
   selectedPlanId,
   onSelect,
   organizationLevel,
   offersTrial = true,
   enterpriseOption = null
}) {
   const levels = OFFERED_LEVELS;
   const [plansByLevel, setPlansByLevel] = useState({});
   const [isLoading, setIsLoading] = useState(true);

   useEffect(() => {
      let isCurrent = true;
      setIsLoading(true);

      Promise.all(levels.map((level) => fetchPlans(level))).then((results) => {
         if (!isCurrent) return;

         setPlansByLevel(
            Object.fromEntries(
               levels.map((level, index) => [level, [...results[index]].sort(byMonthlyPrice)])
            )
         );
         setIsLoading(false);
      });

      return () => {
         isCurrent = false;
      };
   }, [levels]);

   const trackProps = (level) => ({
      level,
      isYearly,
      isLoading,
      selectedPlanId,
      onSelect,
      plans: plansByLevel[level] ?? []
   });

   if (typeof organizationLevel === "number") {
      return (
         <>
            <SingleRowPlans
               isYearly={isYearly}
               isLoading={isLoading}
               selectedPlanId={selectedPlanId}
               onSelect={onSelect}
               plans={levels.flatMap((level) => plansByLevel[level] ?? []).sort(byMonthlyPrice)}
               offersTrial={offersTrial}
               enterpriseOption={enterpriseOption}
            />
            <TaxNote />
         </>
      );
   }

   return (
      <>
         <div className="package-choice__split">
            {levels.map((level) => (
               <PackagePanel key={level} {...trackProps(level)} offersTrial={offersTrial}>
                  {level === AccountLevel.Organization && enterpriseOption}
               </PackagePanel>
            ))}
         </div>
         <TaxNote />
      </>
   );
}

// Packet prices are before tax: Stripe adds any tax to the invoice.
function TaxNote() {
   return (
      <p className="package-choice__tax-note">
         Prices don't include tax. Any applicable taxes are added to your invoice.
      </p>
   );
}

export default PackageChoice;
